import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { wiseRecipientService } from "../../lib/wiseRecipientService";
import { affiliateWiseAccountService } from "../../lib/affiliateWiseAccountService";
import DynamicWiseForm from "../ui/DynamicWiseForm";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Alert from "../ui/Alert";
import {
  X,
  Edit,
  Trash2,
  Plus,
  Loader2,
  Globe,
  CreditCard,
  ArrowRight,
} from "lucide-react";

const WiseRecipientModal = ({
  affiliateName,
  isOpen,
  onClose,
  onSaved,
  title = "Manage Recipients",
  selectable = false,
  onNext,
  onRecipientSelected,
  showCloseButton = true, // New prop to control close button visibility
}) => {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [showDeleteId, setShowDeleteId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedRecipientId, setSelectedRecipientId] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [checkingRegistration, setCheckingRegistration] = useState(true);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedTargetCurrency, setSelectedTargetCurrency] = useState("USD");

  // Curated list of well-supported currencies in Wise
  // These are currencies that are known to have good account type support
  const supportedCurrencies = [
    // Major currencies
    { code: "USD", name: "US Dollar", flag: "🇺🇸" },
    { code: "EUR", name: "Euro", flag: "🇪🇺" },
    { code: "GBP", name: "British Pound", flag: "🇬🇧" },
    { code: "JPY", name: "Japanese Yen", flag: "🇯🇵" },
    { code: "CHF", name: "Swiss Franc", flag: "🇨🇭" },
    
    // North America
    { code: "CAD", name: "Canadian Dollar", flag: "🇨🇦" },
    
    // Asia Pacific
    { code: "AUD", name: "Australian Dollar", flag: "🇦🇺" },
    { code: "NZD", name: "New Zealand Dollar", flag: "🇳🇿" },
    { code: "SGD", name: "Singapore Dollar", flag: "🇸🇬" },
    { code: "HKD", name: "Hong Kong Dollar", flag: "🇭🇰" },
    { code: "INR", name: "Indian Rupee", flag: "🇮🇳" },
    
    // Europe
    { code: "NOK", name: "Norwegian Krone", flag: "🇳🇴" },
    { code: "SEK", name: "Swedish Krona", flag: "🇸🇪" },
    { code: "DKK", name: "Danish Krone", flag: "🇩🇰" },
    { code: "PLN", name: "Polish Zloty", flag: "🇵🇱" },
    { code: "CZK", name: "Czech Koruna", flag: "🇨🇿" },
    { code: "HUF", name: "Hungarian Forint", flag: "🇭🇺" },
    
    // Middle East & Africa
    { code: "AED", name: "UAE Dirham", flag: "🇦🇪" },
    { code: "ZAR", name: "South African Rand", flag: "🇿🇦" },
    
    // South America
    { code: "BRL", name: "Brazilian Real", flag: "🇧🇷" },
    { code: "OMR", name: "Omani Rial", flag: "🇴🇲" },
    { code: "ILS", name: "Israeli Shekel", flag: "🇮🇱" },
    { code: "EGP", name: "Egyptian Pound", flag: "🇪🇬" },
    { code: "ZAR", name: "South African Rand", flag: "🇿🇦" },
    { code: "NGN", name: "Nigerian Naira", flag: "🇳🇬" },
    { code: "KES", name: "Kenyan Shilling", flag: "🇰🇪" },
    { code: "GHS", name: "Ghanaian Cedi", flag: "🇬🇭" },
    { code: "MAD", name: "Moroccan Dirham", flag: "🇲🇦" },
    { code: "BRL", name: "Brazilian Real", flag: "🇧🇷" },
    { code: "ARS", name: "Argentine Peso", flag: "🇦🇷" },
    { code: "CLP", name: "Chilean Peso", flag: "🇨🇱" },
    { code: "COP", name: "Colombian Peso", flag: "🇨🇴" },
    { code: "PEN", name: "Peruvian Sol", flag: "🇵🇪" },
    { code: "UYU", name: "Uruguayan Peso", flag: "🇺🇾" },
    { code: "PKR", name: "Pakistani Rupee", flag: "🇵🇰" },
    { code: "BDT", name: "Bangladeshi Taka", flag: "🇧🇩" },
  ];



  // Check if affiliate is registered and load recipients
  const checkRegistrationAndLoadRecipients = useCallback(async () => {
    if (!affiliateName) return;

    setCheckingRegistration(true);
    setLoading(true);
    setError(null);

    try {
      console.log("🔍 Checking Wise registration for:", affiliateName);
      
      // Check registration status
      const registrationResult = await wiseRecipientService.checkAffiliateRegistration(affiliateName);
      
      if (registrationResult.success) {
        setIsRegistered(registrationResult.isRegistered);
        
        if (registrationResult.isRegistered) {
          // Load recipients
          const recipientsResult = await wiseRecipientService.getRecipients(affiliateName);
          if (recipientsResult.success) {
            setRecipients(recipientsResult.data);
            console.log("✅ Loaded recipients:", recipientsResult.data);
          } else {
            setError("Failed to load recipients: " + recipientsResult.error);
          }
        } else {
          console.log("ℹ️ Affiliate not registered in Wise");
          setRecipients([]);
        }
      } else {
        setError("Failed to check registration: " + registrationResult.error);
      }
    } catch (err) {
      console.error("❌ Error checking registration:", err);
      setError("Failed to check Wise registration");
    } finally {
      setCheckingRegistration(false);
      setLoading(false);
    }
  }, [affiliateName]);

  // Initial setup when modal opens
  useEffect(() => {
    if (isOpen && affiliateName) {
      setEditingId(null);
      setShowDeleteId(null);
      setShowForm(false);
      setSelectedRecipientId(null);
      setSuccessMessage(null);
      
      // Load data
      checkRegistrationAndLoadRecipients();
    }
  }, [isOpen, affiliateName, checkRegistrationAndLoadRecipients]);

  const handleEdit = (recipient) => {
    setEditingId(recipient.id);
    setShowForm(true);
    
    // Set the currency for editing
    if (recipient.currency) {
      setSelectedTargetCurrency(recipient.currency);
    }
  };

  const handleAddNew = () => {
    setEditingId(null);
    setShowForm(true);
    setSelectedTargetCurrency("USD"); // Reset to default
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setShowForm(false);
  };

  // Get initial data for editing mode
  const getEditingInitialData = () => {
    if (!editingId) return {};
    
    const recipient = recipients.find(r => r.id === editingId);
    if (!recipient) return {};

    // Convert Wise recipient data to form format
    const initialData = {
      accountHolderName: recipient.accountHolderName || "",
      // Extract other fields from recipient.details
      ...recipient.details,
      // Set the target currency
      targetCurrency: recipient.currency || selectedTargetCurrency,
    };

    return initialData;
  };

  // Handler for dynamic form submission
  const handleDynamicFormSubmit = async (data) => {
    setSubmitting(true);
    setError(null);

    try {
      console.log("🚀 DYNAMIC WISE RECIPIENT SUBMIT:", data);

      // The dynamic form already provides the correct structure
      // We just need to format it for the Wise API
      const recipientData = {
        accountHolderName: data.accountHolderName || `${data.firstName || ""} ${data.lastName || ""}`.trim(),
        currency: data.targetCurrency || selectedTargetCurrency,
        type: data.accountType || "iban",
        details: {
          legalType: "PRIVATE",
          ...data // Spread all the dynamic form data
        },
      };

      // Remove non-Wise fields from details
      delete recipientData.details.quote;
      delete recipientData.details.sourceCurrency;
      delete recipientData.details.targetCurrency;
      delete recipientData.details.sourceAmount;
      delete recipientData.details.accountType;
      delete recipientData.details.accountHolderName;

      let result;
      if (editingId) {
        result = await wiseRecipientService.updateRecipient(affiliateName, editingId, recipientData);
      } else {
        result = await wiseRecipientService.createRecipient(affiliateName, recipientData);
      }

      if (result.success) {
        // Save the recipient ID to our database for tracking
        console.log('📦 Create result data:', JSON.stringify(result.data), 'editingId:', editingId);
        if (!editingId && result.data?.id) {
          const saveResult = await affiliateWiseAccountService.saveWiseRecipientId(
            affiliateName,
            result.data.id
          );

          if (!saveResult.success || !saveResult.data) {
            console.error('❌ Failed to save recipient ID to database:', saveResult.error);
            setError(`Recipient was created on Wise but failed to save to your account: ${saveResult.error || "No database row was returned."}. Please contact support.`);
            setSubmitting(false);
            return;
          } else {
            console.log('✅ Saved recipient ID to database:', result.data.id);
          }
        }

        // Show success message
        setSuccessMessage(editingId ? "Recipient updated successfully!" : "Recipient created successfully!");
        
        // Reload recipients to show the new one
        await checkRegistrationAndLoadRecipients();
        
        // Reset form state
        setEditingId(null);
        setShowForm(false);
        
        // Call the parent's onSaved callback to update external state
        onSaved && onSaved();
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setSuccessMessage(null);
        setError(`Failed to save recipient: ${result.error}`);
      }
    } catch (err) {
      console.error("❌ DYNAMIC FORM ERROR:", err);
      setSuccessMessage(null);
      setError(`Failed to save recipient: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setSubmitting(true);
    setError(null);

    try {
      const result = await wiseRecipientService.deleteRecipient(affiliateName, id);
      if (result.success) {
        // Also remove from our database
        const removeResult = await affiliateWiseAccountService.removeWiseRecipientId(
          affiliateName, 
          id
        );
        
        if (!removeResult.success) {
          console.error('❌ Failed to remove recipient ID from database:', removeResult.error);
        } else {
          console.log('✅ Removed recipient ID from database:', id);
        }
        
        await checkRegistrationAndLoadRecipients();
        setShowDeleteId(null);
        onSaved && onSaved();
      } else {
        setError("Failed to delete recipient: " + result.error);
      }
    } catch (err) {
      setError("Failed to delete recipient.");
    } finally {
      setSubmitting(false);
    }
  };

  const onClickNext = () => {
    if (selectedRecipientId && onNext) {
      onNext(selectedRecipientId);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#B8460E]/20 rounded-xl flex items-center justify-center">
                <Globe className="h-5 w-5 text-[#B8460E]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{title}</h2>
                <p className="text-gray-400 text-sm">
                  Manage Wise recipients for {affiliateName}
                </p>
              </div>
            </div>
            {showCloseButton && (
              <button
                className="text-gray-400 hover:text-white transition-colors"
                onClick={onClose}
              >
                <X className="h-6 w-6" />
              </button>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 min-h-0">
            {selectable && !showForm && (
              <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <p className="text-blue-300 text-sm">
                  💡 Click on any recipient card to select it for your payout.
                </p>
              </div>
            )}

            {error && <Alert type="error" message={error} />}
            {successMessage && <Alert type="success" message={successMessage} />}

            {checkingRegistration ? (
              <div className="flex justify-center py-12">
                <div className="text-center">
                  <Loader2 className="animate-spin h-8 w-8 text-[#B8460E] mx-auto mb-4" />
                  <p className="text-gray-400">Checking Wise registration...</p>
                </div>
              </div>
            ) : (
              <>
                {!isRegistered && recipients.length === 0 && !showForm ? (
                  <div className="text-center py-12">
                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-[#B8460E]/10 mb-6">
                      <Globe className="h-8 w-8 text-[#B8460E]" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Get Started with Wise
                    </h3>
                    <p className="text-gray-400 mb-6">
                      You're not registered with Wise yet. Add your first bank account to get started.
                    </p>
                    <Button variant="primary" onClick={handleAddNew}>
                      <Plus className="h-4 w-4 mr-1" /> Add New Account
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Add New Button */}
                    <div className="flex justify-end mb-4">
                      {!showForm && (
                        <Button variant="primary" onClick={handleAddNew}>
                          <Plus className="h-4 w-4 mr-1" /> Add New Account
                        </Button>
                      )}
                    </div>

                    {/* Recipients List */}
                    {!showForm && recipients.length > 0 && (
                      <div className="space-y-3 mb-6">
                        {recipients.map((recipient) => {
                          const isSelected = selectable && selectedRecipientId === recipient.id;
                          return (
                            <Card
                              key={recipient.id}
                              className={`p-4 cursor-pointer transition-all duration-200 ${
                                isSelected
                                  ? "ring-2 ring-[#B8460E] bg-[#B8460E]/10"
                                  : "hover:bg-white/5"
                              }`}
                              onClick={() => {
                                if (selectable) {
                                  setSelectedRecipientId(recipient.id);
                                  onRecipientSelected && onRecipientSelected(recipient);
                                }
                              }}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-2">
                                    <CreditCard className="h-5 w-5 text-[#B8460E]" />
                                    <h3 className="font-semibold text-white">
                                      {recipient.accountHolderName}
                                    </h3>
                                    <span className="px-2 py-1 bg-[#B8460E]/20 text-[#B8460E] text-xs rounded-full">
                                      {recipient.currency}
                                    </span>
                                  </div>
                                  <div className="text-sm text-gray-400">
                                    {recipient.details?.address?.country && (
                                      <span>{recipient.details.address.country}</span>
                                    )}
                                    {recipient.details?.accountNumber && (
                                      <span className="ml-2">
                                        ****{recipient.details.accountNumber.slice(-4)}
                                      </span>
                                    )}
                                    {recipient.details?.iban && (
                                      <span className="ml-2">
                                        ****{recipient.details.iban.slice(-4)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(recipient);
                                    }}
                                    className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowDeleteId(recipient.id);
                                    }}
                                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}

                {/* Add/Edit Form */}
                {showForm && (
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-lg p-6 space-y-6 max-h-full overflow-y-auto">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-5 w-5 text-[#B8460E]" />
                        <h3 className="text-lg font-semibold text-white">
                          {editingId ? "Edit Recipient" : "Add New Recipient"}
                        </h3>
                      </div>
                    </div>

                    {/* Currency Selector */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-300">
                        Target Currency <span className="text-red-400">*</span>
                      </label>
                      <select
                        value={selectedTargetCurrency}
                        onChange={(e) => setSelectedTargetCurrency(e.target.value)}
                        className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
                        disabled={editingId} // Disable currency change when editing
                      >
                        {supportedCurrencies.map((currency) => (
                          <option key={currency.code} value={currency.code} className="bg-gray-800">
                            {currency.flag} {currency.code} - {currency.name}
                          </option>
                        ))}

                      </select>
                      {editingId && (
                        <p className="text-xs text-gray-400">
                          Currency cannot be changed when editing existing recipients
                        </p>
                      )}
                    </div>

                    {/* Dynamic Wise Form */}
                    <DynamicWiseForm
                      sourceCurrency="USD"
                      targetCurrency={selectedTargetCurrency}
                      sourceAmount={100}
                      onSubmit={handleDynamicFormSubmit}
                      onCancel={() => {
                        setShowForm(false);
                        setEditingId(null);
                      }}
                      submitting={submitting}
                      initialData={editingId ? getEditingInitialData() : {}}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          {!showForm && (
            <div className="flex justify-end gap-3 pt-6 border-t border-white/10 p-6">
              {selectable ? (
                <>
                  <Button variant="secondary" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    onClick={onClickNext}
                    disabled={!selectedRecipientId}
                    className="bg-[#B8460E] hover:bg-[#8B3A0E] text-white"
                  >
                    Continue with Selected Recipient
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </>
              ) : (
                <Button variant="secondary" onClick={onClose}>
                  Close
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteId && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md mx-4">
              <h3 className="text-lg font-semibold text-white mb-4">
                Delete Recipient
              </h3>
              <p className="text-gray-300 mb-6">
                Are you sure you want to delete this recipient? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowDeleteId(null)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={() => handleDelete(showDeleteId)}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4 mr-2" />
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (typeof document === "undefined") {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
};

export default WiseRecipientModal;
